import { NIGHTMARES } from '@somnhold/content';
import { cellAtWorld, heroWalkable } from '../grid.ts';
import { imax, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, RoofRules, RoofState } from '../types.ts';
import { damageEnemy, ev, findEnemy, spawnEnemy } from './combat.ts';
import { attackHero, stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';
import { sendaCue } from './senda.ts';

/**
 * The roof that tips (docs/sendas/guerrero.md, chapter 3, §4 «El techo que vuelca»; the fort in battle/fort-layout.ts).
 * The fort's gabled roof sits on an axis; each slope's load is what hangs on its eave's hooks and the snow on it, and
 * at SENDA.roof.tip it tips: all the roof's snow falls into that side's yard and buries what is under it (the common
 * nightmares die; the old troll loses a quarter of its life and lies stunned, exposed; a hero under it is stunned a
 * moment), and the eaves are left with their hooks' weight. The wind puts snow on the lee slope, its flag shaking
 * before it turns. The hearth is in one yard: a tip on that side puts it out, and so do the kicks of the nightmares
 * that reach it; out too long in a row loses the night. The nightmares come in at the far yard's mouth and file
 * through the passage at half pace for the hearth: they bunch up in the far yard, which is where the roof should come
 * down. A piece is carried (slower) and hung or taken down standing by its eave.
 *
 * - The test (act 3): the hero works the hooks; the old troll goes straight for the hearth, striking what stands in
 *   front of it; the lit fire frightens it back into the far yard a moment. Under the snow it lies open to blows (out
 *   of it, its hide takes only a little of them). Won when it falls.
 * - The memory (act 2, played as Ruy): the roof is Fermín's. He says to wait while the far yard fills, and tips it
 *   when it is full; he never lets the hearth's side tip. Juana shoots from the stable's roof; Sanne and Tomé light the
 *   hearth again when it goes out (a blow stops them). Lasting the night wins it.
 *
 * Cost: each nightmare asks once a tick which yard it is in (a table by cell, kept off the state); the yards are only
 * walked on a tip.
 */

const YARDS = new WeakMap<RoofRules, Int8Array>();

/** Which yard a cell is in: 0 the hearth's, 1 the far one, 2 the passage, −1 none. */
const yardTable = (s: BattleState, r: RoofRules): Int8Array => {
  let t = YARDS.get(r);
  if (!t) {
    const R = s.map.radius;
    t = new Int8Array((2 * R + 1) * (2 * R + 1)).fill(-1);
    for (const c of r.yards[0]) t[c] = 0;
    for (const c of r.yards[1]) t[c] = 1;
    for (const c of r.passage) t[c] = 2;
    YARDS.set(r, t);
  }
  return t;
};

/** The yard a point stands in (0 the hearth's, 1 the far one, 2 the passage, −1 none). */
export const roofYard = (s: BattleState, x: number, y: number): number => {
  const r = s.rules?.senda?.roof;
  if (!r) return -1;
  const c = cellAtWorld(s, x, y);
  return c < 0 ? -1 : (yardTable(s, r)[c] ?? -1);
};

const fresh = (r: RoofRules): RoofState => ({
  on: r.on.slice(),
  snow: [r.snow[0], r.snow[1]],
  job: -1,
  to: -1,
  held: 0,
  lit: true,
  kicks: 0,
  outAt: -1,
  lighting: 0,
  wind: 0,
  tips: 0,
  tipAt: -1,
  tipSide: -1,
  troll: 0,
  scared: 0,
  buried: 0,
  ferminAt: -1,
  juanaAt: 0,
});

/** What hangs on each eave ([the hearth's, the far one's]) and how many pieces. */
export const roofHung = (r: RoofRules, w: RoofState): { w: [number, number]; n: [number, number] } => {
  const out = { w: [0, 0] as [number, number], n: [0, 0] as [number, number] };
  w.on.forEach((side, i) => {
    if (side !== 0 && side !== 1) return;
    out.w[side] += (r.pieces[i] as { w: number }).w;
    out.n[side]++;
  });
  return out;
};

/** Each slope's load: its hooks and its snow ([the hearth's, the far one's]). */
export const roofLoads = (r: RoofRules, w: RoofState): [number, number] => {
  const h = roofHung(r, w).w;
  return [h[0] + w.snow[0], h[1] + w.snow[1]];
};

/** The hook stand of an eave nearest a point. */
const nearestHook = (r: RoofRules, side: number, x: number, y: number): { x: number; y: number } => {
  let best = r.hooks[side]?.[0] as { x: number; y: number };
  let bd = Infinity;
  for (const p of r.hooks[side] ?? []) {
    const d = Math.abs(p.x - x) + Math.abs(p.y - y);
    if (d < bd) {
      bd = d;
      best = p;
    }
  }
  return best;
};

/** Where piece `p` is picked up from (null: in the hands). */
const whereIs = (r: RoofRules, w: RoofState, p: number, h: Hero): { x: number; y: number } | null => {
  const on = w.on[p] as number;
  if (on === -1) return r.pile;
  if (on === 0 || on === 1) return nearestHook(r, on, h.x, h.y);
  return null;
};

/** Where a piece goes: an eave (0, 1) or the pile (2). */
const spotOf = (r: RoofRules, to: number, h: Hero): { x: number; y: number } => (to === 0 || to === 1 ? nearestHook(r, to, h.x, h.y) : r.pile);

/** Whether an eave takes this piece now (its hooks' hold and its three hooks). */
export const roofTakes = (r: RoofRules, w: RoofState, p: number, side: number): boolean => {
  const hung = roofHung(r, w);
  const wt = (r.pieces[p] as { w: number }).w;
  return (hung.n[side] as number) < r.slots && (hung.w[side] as number) + wt <= r.cap;
};

/** The hearth's mark: the hero sent to it (it lights it standing by it). */
export const ROOF_HEARTH = 99;

/**
 * A tap: piece `at` to eave `to` (0 the hearth's, 1 the far one, 2 the pile), or the hearth (ROOF_HEARTH). The hero
 * goes for the piece (unless it holds it), then where it goes; a piece already in its hands goes back on the pile if
 * another is asked for. In the memory the roof is Fermín's: nothing to tap but the hearth.
 */
export const roofOrder = (s: BattleState, h: Hero, at: number, to: number): void => {
  const r = s.rules?.senda?.roof;
  const w = s.senda?.roof;
  if (!r || !w) return;
  if (at === ROOF_HEARTH) {
    for (let i = 0; i < w.on.length; i++) if (w.on[i] === -2) w.on[i] = -1;
    w.job = -3;
    w.held = 0;
    orderMove(s, h, r.hearth.x + 500, r.hearth.y + 300);
    h.target = 0;
    h.forced = false;
    return;
  }
  if (r.fermin || at < 0 || at >= r.pieces.length || to < 0 || to > 2) return;
  const on = w.on[at] as number;
  if ((to === 2 && on === -1) || on === to) return;
  if (to < 2 && on !== to && !roofTakes(r, { ...w, on: w.on.map((x, i) => (i === at ? -1 : x)) }, at, to)) {
    ev(s, { t: 'senda', kind: 'drop', npc: '', x: h.x, y: h.y, n: to });
    return;
  }
  for (let i = 0; i < w.on.length; i++) if (i !== at && w.on[i] === -2) w.on[i] = -1;
  w.job = at;
  w.to = to;
  w.held = 0;
  const p = on === -2 ? spotOf(r, to, h) : (whereIs(r, w, at, h) as { x: number; y: number });
  orderMove(s, h, p.x, p.y);
  h.target = 0;
  h.forced = false;
};

/** The hero carrying a piece of the roof (its pace: SENDA.roof.carryBp). */
export const roofCarrying = (s: BattleState): boolean => !!s.senda?.roof?.on.includes(-2);

/** The hero's hands: by the piece it takes it, by the eave it hangs it (and the eave may tip there and then). */
const heroWork = (s: BattleState, r: RoofRules, w: RoofState, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job < 0 || h.stun > 0) return;
  const p = w.job;
  const carrying = w.on[p] === -2;
  const at = carrying ? spotOf(r, w.to, h) : whereIs(r, w, p, h);
  if (!at) return;
  if (h.moving || !withinRange(h.x, h.y, at.x, at.y, r.reach)) {
    w.held = 0;
    // Standing still somewhere else (the order cut short by a blow, a stun): on to it again.
    if (!h.moving) orderMove(s, h, at.x, at.y);
    return;
  }
  if (++w.held < r.hang) return;
  w.held = 0;
  if (!carrying) {
    const from = w.on[p] as number;
    w.on[p] = -2;
    if (from === 0 || from === 1) ev(s, { t: 'senda', kind: 'lower', npc: '', x: at.x, y: at.y, n: from * 64 + p });
    else ev(s, { t: 'senda', kind: 'pick', npc: '', x: at.x, y: at.y, n: p });
    const to = spotOf(r, w.to, h);
    orderMove(s, h, to.x, to.y);
    return;
  }
  if (w.to < 2 && !roofTakes(r, w, p, w.to)) {
    // Full by now: back on the pile.
    w.on[p] = -1;
    w.job = -1;
    ev(s, { t: 'senda', kind: 'drop', npc: '', x: at.x, y: at.y, n: w.to });
    return;
  }
  w.on[p] = w.to < 2 ? w.to : -1;
  w.job = -1;
  if (w.to < 2) {
    ev(s, { t: 'senda', kind: 'hang', npc: '', x: at.x, y: at.y, n: w.to * 64 + p });
    checkTip(s, r, w, w.to, cue);
  } else ev(s, { t: 'senda', kind: 'set', npc: '', x: at.x, y: at.y, n: p });
};

/** A slope at its load tips. */
const checkTip = (s: BattleState, r: RoofRules, w: RoofState, side: number, cue: (c: string) => void): void => {
  if ((roofLoads(r, w)[side] as number) >= r.tip) tip(s, r, w, side, cue);
};

/** The roof tips to `side`: all its snow into that yard; what is under it buried; the hearth out if it is that side. */
const tip = (s: BattleState, r: RoofRules, w: RoofState, side: number, cue: (c: string) => void): void => {
  const at = side === 0 ? r.hearth : r.farMid;
  ev(s, { t: 'senda', kind: 'tip', npc: '', x: at.x, y: at.y, n: side });
  w.tips++;
  w.tipAt = s.tick;
  w.tipSide = side;
  cue(`tip${side}`);
  if (w.tips === 1) cue('tip');
  // A little snow slides off and buries nobody (the hearth's fire goes out under it all the same).
  const deep = w.snow[0] + w.snow[1] >= r.bury;
  for (const e of s.enemies) {
    if (!deep) break;
    if (e.hp <= 0 || roofYard(s, e.x, e.y) !== side) continue;
    if (e.id === w.troll) {
      w.buried = r.stun;
      e.stun = imax(e.stun, r.stun);
      damageEnemy(s, e, mulBp(e.maxHp, r.quarterBp), { pid: '', ignoresArmor: true });
      ev(s, { t: 'senda', kind: 'bury', npc: '', x: e.x, y: e.y });
      cue('buried');
      continue;
    }
    damageEnemy(s, e, e.maxHp * 4, { pid: '', ignoresArmor: true });
    if (e.hp > 0) e.hp = 0;
  }
  for (const h of s.heroes) if (deep && h.downTicks === 0 && roofYard(s, h.x, h.y) === side) h.stun = imax(h.stun, r.heroStun);
  if (side === 0 && w.lit) {
    putOut(s, r, w, cue);
    w.snowed = true;
  }
  w.snow = [0, 0];
};

const putOut = (s: BattleState, r: RoofRules, w: RoofState, cue: (c: string) => void): void => {
  w.lit = false;
  w.kicks = 0;
  w.outAt = s.tick;
  w.lighting = 0;
  ev(s, { t: 'senda', kind: 'out', npc: '', x: r.hearth.x, y: r.hearth.y });
  cue('out');
};

/** The wind: its turns (the flag shakes before), and one of snow on the lee slope every so often. */
const windTick = (s: BattleState, r: RoofRules, w: RoofState, cue: (c: string) => void): void => {
  const t = s.waveT;
  while (w.wind + 1 < r.wind.length && (r.wind[w.wind + 1] as { t: number }).t <= t) {
    w.wind++;
    const to = (r.wind[w.wind] as { to: number }).to;
    ev(s, { t: 'senda', kind: 'wind', npc: '', x: r.farMid.x, y: r.farMid.y, n: to });
    if (to === 0) cue('windHearth');
  }
  const next = r.wind[w.wind + 1];
  if (next && t === next.t - r.warn) ev(s, { t: 'senda', kind: 'windWarn', npc: '', x: r.farMid.x, y: r.farMid.y, n: next.to });
  if (t <= 0 || t % r.every !== 0) return;
  const lee = (r.wind[w.wind] as { to: number }).to;
  // The memory: Fermín never lets the hearth's side tip (he sweeps it); its snow stops short.
  if (r.fermin && lee === 0 && (roofLoads(r, w)[0] as number) + 1 >= r.tip) return;
  w.snow[lee] = (w.snow[lee] as number) + 1;
  checkTip(s, r, w, lee, cue);
};

/** The hearth: who stands by it lights it again (the hero, or in the memory Sanne or Tomé); out too long loses. */
const hearthTick = (s: BattleState, r: RoofRules, w: RoofState, cue: (c: string) => void): void => {
  if (w.lit) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  let by = !!h && !h.moving && h.stun <= 0 && withinRange(h.x, h.y, r.hearth.x, r.hearth.y, r.reach);
  if (!by && r.fermin) {
    // Sanne first, then Tomé: whoever isn't down walks to the fire and lights it (a blow stops them a moment).
    for (const id of ['sanne', 'tome']) {
      const n = s.senda?.npcs.find((x) => x.id === id && x.hp > 0);
      if (!n) continue;
      if (n.flinch > 0) break;
      if (!withinRange(n.x, n.y, r.hearth.x, r.hearth.y, 700)) {
        n.moving = true;
        stepToward(n, r.hearth.x + 300, r.hearth.y + 200, 70);
        break;
      }
      by = true;
      break;
    }
  }
  if (!by) {
    w.lighting = 0;
    return;
  }
  if (++w.lighting >= (w.snowed ? r.dig : r.light)) {
    w.snowed = false;
    w.lit = true;
    w.kicks = 0;
    w.outAt = -1;
    w.lighting = 0;
    if (w.job === -3) w.job = -1;
    ev(s, { t: 'senda', kind: 'lit', npc: '', x: r.hearth.x, y: r.hearth.y });
    cue('lit');
  }
};

/** How many nightmares stand in the far yard. */
export const roofFarCount = (s: BattleState): number => {
  let n = 0;
  for (const e of s.enemies) if (e.hp > 0 && roofYard(s, e.x, e.y) === 1) n++;
  return n;
};

/** Fermín on the ridge (the memory): wait while it fills, tip it when it's full. */
const ferminTick = (s: BattleState, r: RoofRules, w: RoofState, cue: (c: string) => void): void => {
  const F = r.fermin;
  if (!F) return;
  if (w.ferminAt >= 0) {
    if (s.tick >= w.ferminAt) {
      w.ferminAt = -1;
      tip(s, r, w, 1, cue);
    }
    return;
  }
  const n = roofFarCount(s);
  if (n >= F.gather) cue('gather');
  if (n >= F.at && w.snow[1] + w.snow[0] >= 2) {
    cue('now');
    w.ferminAt = s.tick + 12;
  }
};

/** Juana from the stable's roof: a shot every so often at the one nearest the passage in the far yard. */
const juanaTick = (s: BattleState, r: RoofRules, w: RoofState): void => {
  const J = r.juana;
  const j = s.senda?.npcs.find((n) => n.id === 'juana');
  if (!J || !j || s.tick < w.juanaAt) return;
  const gate = r.route[3] as { x: number; y: number };
  let best: Enemy | null = null;
  let bd = Infinity;
  for (const e of s.enemies) {
    if (e.hp <= 0 || roofYard(s, e.x, e.y) !== 1) continue;
    const d = Math.abs(e.x - gate.x) + Math.abs(e.y - gate.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  if (!best) return;
  w.juanaAt = s.tick + J.every;
  ev(s, { t: 'senda', kind: 'shot', npc: 'juana', x: best.x, y: best.y });
  damageEnemy(s, best, J.hit, { pid: '', fromX: j.x, fromY: j.y });
};

/** The old troll comes in its time. */
const trollTick = (s: BattleState, r: RoofRules, w: RoofState, cue: (c: string) => void): void => {
  const T = r.troll;
  if (!T) return;
  if (!w.troll && s.waveT >= T.at) {
    const p = r.route[0] as { x: number; y: number };
    const e = spawnEnemy(s, 'troll', 1, false, 0, p.x, p.y, T.hp);
    e.speed = mulBp(e.speed, T.speedBp);
    e.damage = mulBp(e.damage, T.dmgBp);
    e.leg = 1;
    w.troll = e.id;
    cue('trollIn');
  }
  if (w.buried > 0) w.buried--;
  // The old troll doesn't knit back as the young ones do: what the roof and the blows take stays taken.
  const e = w.troll ? findEnemy(s, w.troll) : null;
  if (e && e.hp > 0) e.scorch = s.tick;
};

/** Steps toward a point unless that would put it on ground nobody walks (the fort, the walls). */
const walk = (s: BattleState, e: Enemy, x: number, y: number, px: number): boolean => {
  const ox = e.x;
  const oy = e.y;
  const done = stepToward(e, x, y, px);
  if (!heroWalkable(s, cellAtWorld(s, e.x, e.y))) {
    e.x = ox;
    e.y = oy;
    return false;
  }
  return done;
};

const REACH_HERO = 1400;

/**
 * A nightmare's turn on the fort (true: handled): the old troll by its own rule; the rest go down their way for the
 * hearth, fight the hero who stands in it, and kick the lit hearth when they reach it.
 */
export const roofFoe = (s: BattleState, e: Enemy, px: number): boolean => {
  const r = s.rules?.senda?.roof;
  const w = s.senda?.roof;
  if (!r || !w) return false;
  // (The passage holds the common ones to single file; the old troll shoulders through.)
  const yard = roofYard(s, e.x, e.y);
  if (yard === 2 && e.id !== w.troll) px = mulBp(px, r.slowBp);
  const h = s.heroes.find((x) => x.downTicks === 0 && x.light === undefined);
  if (e.id === w.troll) return trollMove(s, r, w, e, px, h);
  // Taunted, or the hero in its way: at the hero.
  const taunted = e.taunter > 0 ? s.heroes.find((x) => x.id === e.taunter && x.downTicks === 0) : undefined;
  const foe = taunted ?? (h && withinRange(h.x, h.y, e.x, e.y, REACH_HERO) ? h : undefined);
  if (foe) {
    e.anim = 'walk';
    if (withinRange(foe.x, foe.y, e.x, e.y, NIGHTMARES[e.kind].reach + 150)) attackHero(s, e, foe);
    else walk(s, e, foe.x, foe.y, px);
    return true;
  }
  const leg = e.leg ?? 1;
  const p = r.route[leg];
  if (p) {
    e.anim = 'walk';
    // Spread a little across the way so a band doesn't walk as one.
    const off = ((e.id * 37) % 5) * 120 - 240;
    if (walk(s, e, p.x + off, p.y + ((e.id * 53) % 5) * 90 - 180, px) || withinRange(e.x, e.y, p.x + off, p.y, 200)) e.leg = leg + 1;
    return true;
  }
  // At the hearth.
  if (!withinRange(e.x, e.y, r.hearth.x, r.hearth.y, 800)) {
    e.anim = 'walk';
    walk(s, e, r.hearth.x, r.hearth.y, px);
    return true;
  }
  e.facing = 0;
  if (w.lit && e.cd === 0) {
    e.cd = 12;
    e.anim = 'attack';
    e.animT = 0;
    w.kicks++;
    ev(s, { t: 'senda', kind: 'kick', npc: '', x: r.hearth.x, y: r.hearth.y, n: w.kicks });
    if (w.kicks >= r.kicks) putOut(s, r, w, (c) => sendaCue(s, c));
  } else if (!w.lit) e.anim = 'idle';
  return true;
};

/** The old troll: scared off by the fire, buried by the snow, else straight on for the hearth over whatever is in front. */
const trollMove = (s: BattleState, r: RoofRules, w: RoofState, e: Enemy, px: number, h: Hero | undefined): boolean => {
  const T = r.troll;
  if (!T) return false;
  if (w.scared > 0) {
    // Back the way it came (toward the far yard), as far as the fright lasts: one waypoint back at a time.
    w.scared--;
    e.anim = 'walk';
    const back = imax(2, (e.leg ?? 2) - 1);
    const p = r.route[back] as { x: number; y: number };
    if (walk(s, e, p.x, p.y, mulBp(px, T.fleeBp)) || withinRange(e.x, e.y, p.x, p.y, 200)) e.leg = back;
    return true;
  }
  if (w.lit && withinRange(e.x, e.y, r.hearth.x, r.hearth.y, T.fear)) {
    w.scared = T.scare;
    ev(s, { t: 'senda', kind: 'scare', npc: '', x: e.x, y: e.y });
    sendaCue(s, 'scared');
    return true;
  }
  const taunted = e.taunter > 0 ? s.heroes.find((x) => x.id === e.taunter && x.downTicks === 0) : undefined;
  if (taunted) {
    e.anim = 'walk';
    if (withinRange(taunted.x, taunted.y, e.x, e.y, NIGHTMARES[e.kind].reach + 150)) attackHero(s, e, taunted);
    else walk(s, e, taunted.x, taunted.y, px);
    return true;
  }
  // What stands in front of it gets crushed; at a cold hearth, what it crushes is the hero.
  if (h && withinRange(h.x, h.y, e.x, e.y, NIGHTMARES[e.kind].reach + 100)) attackHero(s, e, h);
  if (h && !w.lit && withinRange(e.x, e.y, r.hearth.x, r.hearth.y, 2600)) {
    e.anim = 'walk';
    if (!withinRange(h.x, h.y, e.x, e.y, NIGHTMARES[e.kind].reach + 100)) walk(s, e, h.x, h.y, px);
    return true;
  }
  const leg = e.leg ?? 1;
  const p = r.route[leg] ?? r.hearth;
  e.anim = 'walk';
  if (withinRange(e.x, e.y, r.hearth.x, r.hearth.y, 700)) {
    e.anim = e.cd > 0 ? e.anim : 'idle';
    return true;
  }
  if (walk(s, e, p.x, p.y, px) || withinRange(e.x, e.y, p.x, p.y, 200)) e.leg = leg + 1;
  return true;
};

export const roofTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.roof;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.roof ??= fresh(r));
  if (!s.started) return;
  for (const n of st.npcs) {
    n.moving = false;
    if (n.flinch > 0) n.flinch--;
  }
  trollTick(s, r, w, cue);
  heroWork(s, r, w, cue);
  windTick(s, r, w, cue);
  ferminTick(s, r, w, cue);
  juanaTick(s, r, w);
  hearthTick(s, r, w, cue);
  // Sanne and Tomé back to their places by the fire when it burns.
  if (r.fermin && w.lit) {
    for (const n of st.npcs) {
      if (n.id !== 'sanne' && n.id !== 'tome') continue;
      const home = s.rules?.senda?.npcs.find((x) => x.id === n.id);
      if (home && n.hp > 0 && n.flinch <= 0 && !withinRange(n.x, n.y, home.x, home.y, 100)) {
        n.moving = true;
        stepToward(n, home.x, home.y, 60);
      }
    }
  }
  // The first ones out of the passage into the hearth's yard.
  if (!st.cues?.includes('through')) for (const e of s.enemies) if (e.hp > 0 && roofYard(s, e.x, e.y) === 0) cue('through');
  // The old troll down: the roof tips one last time, on its own, and the sky clears through the snow.
  const troll = w.troll ? findEnemy(s, w.troll) : null;
  if (r.troll && w.troll && (!troll || troll.hp <= 0) && w.tipSide !== 3) {
    w.tipSide = 3;
    ev(s, { t: 'senda', kind: 'tip', npc: '', x: r.farMid.x, y: r.farMid.y, n: 3 });
    w.snow = [0, 0];
  }
};

/** The test: the old troll's life taken; the memory: the night's clock (battle/senda.ts reads it as time). */
export const roofGoal = (s: BattleState): { done: number; need: number } => {
  const w = s.senda?.roof;
  const e = w?.troll ? findEnemy(s, w.troll) : null;
  if (!w?.troll) return { done: 0, need: 1 };
  if (!e) return { done: 1, need: 1 };
  return { done: e.maxHp - imax(0, e.hp), need: e.maxHp };
};

/** Lost with the hearth out too long in a row; the test won when the old troll falls. */
export const roofResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.roof;
  const w = s.senda?.roof;
  if (!r || !w) return null;
  if (!w.lit && w.outAt >= 0 && s.tick - w.outAt >= r.out) return 'lost';
  if (r.troll && w.troll) {
    const e = findEnemy(s, w.troll);
    if (!e || e.hp <= 0) return 'won';
  }
  return null;
};
